import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Check, X, MessageSquare, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import MockupApprovalTimeline from '../components/MockupApprovalTimeline';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

const MultiSidePreview = ({ views, sides }) => {
  const [active, setActive] = useState(sides[0]);
  return (
    <div className="mb-6">
      <div className="flex justify-center gap-2 mb-3">
        {sides.map(s => (
          <button key={s}
            onClick={() => setActive(s)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${active === s ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            data-testid={`approval-side-tab-${s}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ minHeight: 400 }}>
        <img src={views[active]} alt={active} className="w-full h-full object-contain" style={{ maxHeight: 500 }} />
      </div>
    </div>
  );
};

const DesignApproval = () => {
  const { proposalId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [proposal, setProposal] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchProposal();
  }, [proposalId, token]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`/public/design-approval/${proposalId}?token=${token}`);
      setProposal(response.data);
    } catch (error) {
      toast.error('Invalid or expired approval link');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post(`/public/design-approval/${proposalId}/respond`, {
        token,
        item_index: currentItemIndex,
        approved: true
      });
      
      // Update local state
      const updatedItems = [...proposal.items];
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        approval_status: 'approved'
      };
      setProposal({ ...proposal, items: updatedItems });
      
      toast.success('Design approved! Thank you.');
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!feedback.trim()) {
      toast.error('Please describe the changes you need');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`/public/design-approval/${proposalId}/respond`, {
        token,
        item_index: currentItemIndex,
        approved: false,
        feedback
      });
      
      // Update local state
      const updatedItems = [...proposal.items];
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        approval_status: 'changes_requested',
        approval_feedback: feedback
      };
      setProposal({ ...proposal, items: updatedItems });
      
      toast.success('Change request submitted. We will update your design.');
      setShowFeedbackForm(false);
      setFeedback('');
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit change request');
    } finally {
      setSubmitting(false);
    }
  };

  const currentItem = proposal?.items?.[currentItemIndex];
  const hasMultipleItems = proposal?.items?.length > 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg">
          <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
          <p className="text-gray-600">This approval link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Design Approval</h1>
            <p className="text-sm text-gray-600">Order #{proposal.proposal_number}</p>
          </div>
          <div className="text-right">
            <p className="font-medium">{proposal.customer_name}</p>
            <p className="text-sm text-gray-600">{proposal.customer_email}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Item Navigation */}
        {hasMultipleItems && (
          <div className="flex items-center justify-center gap-4 mb-6">
            <Button
              variant="outline"
              size="sm"
              disabled={currentItemIndex === 0}
              onClick={() => setCurrentItemIndex(currentItemIndex - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium">
              Item {currentItemIndex + 1} of {proposal.items.length}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentItemIndex >= proposal.items.length - 1}
              onClick={() => setCurrentItemIndex(currentItemIndex + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Product Info */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
            <h2 className="text-lg font-semibold">
              {currentItem?.brand} - {currentItem?.style}
            </h2>
            <p className="text-purple-100">
              Color: {currentItem?.color} | Qty: {currentItem?.quantity}
            </p>
          </div>

          {/* Mockup Preview */}
          <div className="p-6">
            {(() => {
              const views = currentItem?.mockup_views;
              const sides = views ? Object.keys(views).filter(k => views[k]) : [];
              const [activeSide, setActiveSide] = [sides[0], () => {}];
              if (sides.length > 0) {
                return (
                  <MultiSidePreview views={views} sides={sides} />
                );
              }
              return (
                <div className="bg-gray-100 rounded-lg overflow-hidden mb-6" style={{ minHeight: '400px' }}>
                  {currentItem?.mockup_url ? (
                    <img
                      src={currentItem.mockup_url}
                      alt="Design mockup"
                      className="w-full h-full object-contain"
                      style={{ maxHeight: '500px' }}
                    />
                  ) : currentItem?.design_file ? (
                    <div className="flex flex-col items-center justify-center h-full p-8">
                      <img
                        src={currentItem.design_file}
                        alt="Design"
                        className="max-w-full max-h-64 object-contain mb-4"
                      />
                      <p className="text-gray-600 text-sm">Design file for this item</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-400">
                      <p>No mockup available for this item</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Current Status */}
            {currentItem?.approval_status && (
              <div className={`mb-6 p-4 rounded-lg ${
                currentItem.approval_status === 'approved' 
                  ? 'bg-green-50 border border-green-200' 
                  : currentItem.approval_status === 'changes_requested'
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center gap-2">
                  {currentItem.approval_status === 'approved' && (
                    <Check className="w-5 h-5 text-green-600" />
                  )}
                  {currentItem.approval_status === 'changes_requested' && (
                    <MessageSquare className="w-5 h-5 text-yellow-600" />
                  )}
                  <span className={`font-medium ${
                    currentItem.approval_status === 'approved' 
                      ? 'text-green-800' 
                      : currentItem.approval_status === 'changes_requested'
                      ? 'text-yellow-800'
                      : 'text-blue-800'
                  }`}>
                    {currentItem.approval_status === 'approved' && 'You approved this design'}
                    {currentItem.approval_status === 'changes_requested' && 'Changes requested'}
                    {currentItem.approval_status === 'pending' && 'Awaiting your approval'}
                  </span>
                </div>
                {currentItem.approval_feedback && (
                  <p className="mt-2 text-sm text-gray-600">
                    Your feedback: "{currentItem.approval_feedback}"
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            {(!currentItem?.approval_status || currentItem?.approval_status === 'pending') && !submitted && (
              <div>
                {showFeedbackForm ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        What changes do you need?
                      </label>
                      <Textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Please describe the changes you'd like us to make to this design..."
                        className="min-h-[120px]"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleRequestChanges}
                        disabled={submitting || !feedback.trim()}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <MessageSquare className="w-4 h-4 mr-2" />
                        )}
                        Submit Change Request
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowFeedbackForm(false)}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    <Button
                      onClick={handleApprove}
                      disabled={submitting}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-lg py-6"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Check className="w-5 h-5 mr-2" />
                      )}
                      Approve Design
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowFeedbackForm(true)}
                      disabled={submitting}
                      className="flex-1 text-lg py-6 border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                    >
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Request Changes
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Already Approved/Submitted */}
            {(currentItem?.approval_status === 'approved' || submitted) && (
              <div className="text-center py-8">
                <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-green-700 mb-2">Thank You!</h3>
                <p className="text-gray-600">
                  {currentItem?.approval_status === 'approved' 
                    ? 'This design has been approved and is ready for production.'
                    : 'Your response has been recorded. We will be in touch soon.'}
                </p>
              </div>
            )}

            {/* Full audit trail for the customer too */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <MockupApprovalTimeline
                proposalId={proposalId}
                itemIndex={currentItemIndex}
                publicToken={token}
              />
            </div>
          </div>
        </div>

        {/* Download Design */}
        {currentItem?.mockup_url && (
          <div className="text-center">
            <a 
              href={currentItem.mockup_url} 
              download={`mockup-${proposal.proposal_number}-item${currentItemIndex + 1}.png`}
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700"
            >
              <Download className="w-4 h-4" />
              Download mockup image
            </a>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-sm text-gray-500">
        <p>{brandName()}</p>
        <p>Questions? Contact us at {brandEmail()}</p>
      </footer>
    </div>
  );
};

export default DesignApproval;
